/**
 * i18n — tiny localisation layer. Strings are keyed by their ENGLISH text, so a
 * component just wraps text in `t("...")`. If the current language is English (or
 * a Thai translation is missing) the English text is returned unchanged, so the
 * UI never breaks. `useLang()` re-renders a component when the language changes.
 */
import { useEffect, useState } from "react";
import { TH } from "./i18n.th";

export type Lang = "en" | "th";

let lang: Lang = "en";
try {
  const saved = localStorage.getItem("space-adventura-lang");
  if (saved === "th" || saved === "en") lang = saved;
} catch {
  /* storage unavailable */
}

const listeners = new Set<() => void>();

export function getLang(): Lang {
  return lang;
}
export function setLang(l: Lang): void {
  if (l === lang) return;
  lang = l;
  try {
    localStorage.setItem("space-adventura-lang", l);
  } catch {
    /* ignore */
  }
  for (const fn of listeners) fn();
}
export function toggleLang(): void {
  setLang(lang === "en" ? "th" : "en");
}

/** Translate an English string to the current language (English fallback). */
export function t(en: string): string {
  if (lang === "en") return en;
  return TH[en] ?? en;
}

/** Subscribe to language changes in a React component; returns the current lang. */
export function useLang(): Lang {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = (): void => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return lang;
}
